import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Accordion, Divider } from 'wix-ui-tpa/cssVars';
import { FormSelectedSlot } from '../../../../types/formSelectedSlots';
import { getServiceSlotIdentifier } from '../../../../utils';
import { getContent } from '../../../../utils/content/content';
import {
  SelectedProduct,
  ServiceData,
  V3Product,
} from '../../../../utils/state/types';
import settingsParams from '../../settingsParams';
import Text, { TextType } from '../../Text/Text';
import { classes, st } from './BookingDetails.st.css';
import { BookingDetailsDataHook } from './dataHooks';
import SingleSlotDetails from './SingleSlotDetails/SingleSlotDetails';
import MultiParticipantDetails from './MultiParticipantDetails/MultiParticipantDetails';
import { getParticipants } from '../../../../utils/multiParticipantFlow/participants';

export interface BookingDetailsProps {
  serviceData: ServiceData;
  formSelectedSlot: FormSelectedSlot;
  numberOfParticipants?: number;
  isCart: boolean | undefined;
  selectedProductAddons?: SelectedProduct[];
  relatedProducts?: V3Product[];
}

const SERVICE_DETAILS_TITLE = 'service-details';

const BookingDetails: React.FC<BookingDetailsProps> = ({
  serviceData,
  formSelectedSlot,
  numberOfParticipants,
  isCart,
  selectedProductAddons,
  relatedProducts,
}) => {
  const { isMobile } = useEnvironment();
  const settings = useSettings();

  const { isSingleService, slotServices } = serviceData;
  const [firstSlot] = formSelectedSlot.nestedSlots;
  const firstSlotService = firstSlot
    ? slotServices[getServiceSlotIdentifier(firstSlot)]
    : undefined;
  const participants = isSingleService ? getParticipants(firstSlotService) : [];
  const hasMultipleParticipants = participants.length > 1;

  const sectionTitle = getContent({
    settings,
    settingsParam: settingsParams.summarySectionTitle,
  });

  const tagName = settings.get(settingsParams.summarySectionTagName);

  const renderMobile = () => (
    <Accordion
      className={classes.accordion}
      data-hook={BookingDetailsDataHook.ACCORDION}
    >
      <Accordion.Item
        id={SERVICE_DETAILS_TITLE}
        title={sectionTitle}
        titleTag={tagName}
      >
        {renderContent()}
      </Accordion.Item>
    </Accordion>
  );

  const renderDesktop = () => (
    <>
      <Text
        tagName={tagName}
        type={TextType.Title}
        data-hook={BookingDetailsDataHook.SECTION_TITLE}
      >
        {sectionTitle}
      </Text>
      <Divider className={st(classes.divider, classes.divider1)} />
      {renderContent()}
    </>
  );

  const renderParticipants = () => (
    <MultiParticipantDetails
      slot={firstSlot}
      slotService={slotServices[getServiceSlotIdentifier(firstSlot)]}
      selectedProductAddons={selectedProductAddons}
      relatedProducts={relatedProducts}
      alwaysExpanded={isMobile}
    />
  );

  const renderContent = () =>
    hasMultipleParticipants
      ? renderParticipants()
      : formSelectedSlot.nestedSlots.map((slot, index, slots) => {
          const slotId = getServiceSlotIdentifier(slot);
          const slotService = slotServices[slotId];
          const isLastSlot = index === slots.length - 1;
          return (
            <React.Fragment key={slotId}>
              <div className={index < slots.length ? classes.item : ''}>
                <SingleSlotDetails
                  isSingleService={isSingleService}
                  slot={slot}
                  slotService={slotService}
                  totalNumberOfParticipants={numberOfParticipants}
                  isCart={isCart}
                  selectedProductAddons={selectedProductAddons}
                  relatedProducts={relatedProducts}
                />
              </div>
              {!isSingleService && !isLastSlot && (
                <Divider
                  className={st(classes.divider, classes.multiServiceDivider)}
                />
              )}
            </React.Fragment>
          );
        });

  return (
    <div
      className={st(classes.root, {
        isMobile,
        isMultiServices: !isSingleService,
      })}
    >
      {isMobile ? renderMobile() : renderDesktop()}
    </div>
  );
};

export default BookingDetails;
